import { PROD_ENT, SESSION, REGISTER, OFFLINE_ID, QUEUE } from '../../../constants';
import {
  batchRequest,
  createRequest,
  getRequest,
  listRequest,
  moveRequest,
  reportRequest,
  resetRequest,
  updateRequest,
} from 'core/services/admin';
import { all, call, fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { RootState } from 'types';
import { hydrate, IPersist, persist } from 'core/actions/product/state';
import {
  actionRequest,
  attemptRequest,
  dialogueRequest,
  homeRequest,
  moduleRequest,
  suspenseRequest,
  tokenRequest,
  verificationRequest,
} from 'core/services/product';
import { clearCache, clearError, logout, restartToken, updateToken, updateTokenState } from 'core/slices/session';
import {
  authRequest,
  authGoogleRequest,
  validationRequest,
  passwordRequest,
  systemSigninRequest,
  newSigninRequest,
} from 'core/services/authentication';
import { PayloadAction } from '@reduxjs/toolkit';
import { clearCookies } from '../auth/auth';
import { EncryptStorage } from 'encrypt-storage';
import {
  decrementIndex,
  goToIndex,
  incrementIndex,
  preview,
  registerAttempt,
  updateVerification,
} from 'core/slices/register';
import { generateOfflineId } from 'core/slices/offline';
import { update } from 'core/actions/admin/data';
import { EntityTypes } from 'constants/entities';
import { queueItem } from 'core/slices/queue';
import { env } from 'utils/env';

export const encryptStorage = new EncryptStorage(env.SECRET, {});

function* init() {
  if (!encryptStorage || !encryptStorage.getItem) return;

  try {
    const prodEnt = yield call([encryptStorage, encryptStorage.getItem], PROD_ENT);
    const session = yield call([encryptStorage, encryptStorage.getItem], SESSION);
    const register = yield call([encryptStorage, encryptStorage.getItem], REGISTER);
    const queue = yield call([encryptStorage, encryptStorage.getItem], QUEUE);
    const offlineId = yield call([encryptStorage, encryptStorage.getItem], OFFLINE_ID);

    yield put(
      yield call(hydrate, {
        prodEnt: prodEnt !== undefined ? prodEnt : null,
        session: session !== undefined ? session : null,
        register: register !== undefined ? register : null,
        queue: queue !== undefined ? queue : null,
        offlineId,
      }),
    );
  } catch (e) {
    console.log('persist hyrdation error', e);
  }
}

function* updateProdEntSaga() {
  const prodEnt = yield select((state: RootState) =>
    Object.keys(state.entity.present).reduce((prev, next) => {
      return [
        EntityTypes.COURSE_PROGRESS,
        EntityTypes.ORG_USER,
        EntityTypes.ORG_USER_VARIABLE,
        EntityTypes.ORG_USER_VARIABLE_OVER,
      ].includes(next as EntityTypes)
        ? prev
        : { ...prev, [next]: state.entity.present[next] };
    }, {}),
  );
  try {
    yield call([encryptStorage, encryptStorage.setItem], PROD_ENT, prodEnt);
  } catch (e) {
    console.log('course not set prodEnt storage', e);
  }
}

function* updateSessionSaga() {
  const session = yield select((state: RootState) => state.session);
  try {
    yield call([encryptStorage, encryptStorage.setItem], SESSION, session);
  } catch (e) {
    console.log('course not set session storage', e);
  }
}

function* updateRegisterSaga() {
  const register = yield select((state: RootState) => state.register);
  const queue = yield select((state: RootState) => state.queue);

  try {
    yield call([encryptStorage, encryptStorage.setItem], REGISTER, register);
    yield call([encryptStorage, encryptStorage.setItem], QUEUE, queue);
  } catch (e) {
    console.log('course not set register storage', e);
  }
}

function* persistActionSaga(action: PayloadAction<Partial<IPersist>>) {
  if (!encryptStorage || !encryptStorage.setItem) return;

  if (action.payload.prodEnt) yield updateProdEntSaga();
  if (action.payload.session) yield updateSessionSaga();
  if (action.payload.register) yield updateRegisterSaga();
}

function* clearCacheSaga() {
  if (!window.localStorage || !window.localStorage.clear) return;

  try {
    yield call([window.localStorage, window.localStorage.clear]);
    yield clearCookies();
  } catch (e) {}
}

function* updateProdEnt() {
  yield takeLatest(
    [
      update,
      homeRequest.fulfilled,
      tokenRequest.fulfilled,
      getRequest.fulfilled,
      updateRequest.fulfilled,
      moduleRequest.fulfilled,
      reportRequest.fulfilled,
      listRequest.fulfilled,
      homeRequest.fulfilled,
      verificationRequest.fulfilled,
      validationRequest.fulfilled,
      actionRequest.fulfilled,
      createRequest.fulfilled,
      moveRequest.fulfilled,
      resetRequest.fulfilled,
      batchRequest.fulfilled,
    ],
    updateProdEntSaga,
  );
}

function* offlineIdSaga() {
  if (!encryptStorage || !encryptStorage.setItem) return;

  const offlineId = yield select((state: RootState) => state.core.offline.id);
  yield call([encryptStorage, encryptStorage.setItem], OFFLINE_ID, offlineId);
}

function* watchClearCache() {
  yield takeLatest(clearCache, clearCacheSaga);
}

function* persistAction() {
  yield takeLatest(persist, persistActionSaga);
}

function* updateSession() {
  yield takeLatest(
    [
      homeRequest.fulfilled,
      tokenRequest.fulfilled,
      systemSigninRequest.fulfilled,
      updateToken,
      updateTokenState,
      authRequest.fulfilled,
      authGoogleRequest.fulfilled,
      validationRequest.fulfilled,
      passwordRequest.fulfilled,
      newSigninRequest.fulfilled,
      clearError,
      logout,
    ],
    updateSessionSaga,
  );
}

function* watchGenerateOfflineId() {
  yield takeLatest([generateOfflineId, registerAttempt, preview], offlineIdSaga);
}

function* updateRegister() {
  yield takeEvery(
    [
      updateVerification,
      registerAttempt,
      incrementIndex,
      decrementIndex,
      goToIndex,
      tokenRequest.fulfilled,
      actionRequest.fulfilled,
      homeRequest.fulfilled,
      attemptRequest.fulfilled,
      dialogueRequest.fulfilled,
      restartToken,
      queueItem,
      suspenseRequest.pending,
    ],
    updateRegisterSaga,
  );
}

export default function* persistSaga() {
  yield all([
    fork(init),
    //Remove product to avoid caching issues
    fork(updateProdEnt),
    fork(updateSession),
    fork(updateRegister),
    fork(persistAction),
    fork(watchClearCache),
    fork(watchGenerateOfflineId),
  ]);
}
